import { useState } from "react";
import { chainStore } from "@/stores/chain/store";
import * as chainNav from "@/modules/chainNav";
import { type ChainNavMeta } from "@/modules/chainNav";
import { chainThumbUrl } from "@/modules/chainThumb";
import { cleanChainTitle } from "@/modules/chainTitle";
import { eventNames } from "@/modules/Constants";
import * as analytics from "@/modules/analytics";
import type ChainNavButton from "@/common/videojs/ChainNavButton";
import type { NavPreview, VideoJsSelf } from "@/components/player/videoJsTypes";

const PREV_SVG =
  '<svg viewBox="0 0 24 24" width="100%" height="100%" fill="currentColor" aria-hidden="true"><rect x="4" y="3" width="3.4" height="18"/><path d="M21 3v18L8 12z"/></svg>';
const NEXT_SVG =
  '<svg viewBox="0 0 24 24" width="100%" height="100%" fill="currentColor" aria-hidden="true"><path d="M3 3v18l13-9z"/><rect x="16.6" y="3" width="3.4" height="18"/></svg>';

// Chain prev/next arrows in the control bar and their hover previews.
function createChainNavigation(self: VideoJsSelf) {
  // Add the chain prev/next arrows to the control bar, right after the play
  // toggle (so they sit between play and volume). Idempotent.
  function setupChainNavButtons() {
    if (!self.player || !self.player.controlBar || self.prevNavBtn) {
      return;
    }
    const cb = self.player.controlBar;
    // Insert straight after the play toggle; fall back to index 1.
    const playToggle = cb.getChild("playToggle");
    const playIndex = playToggle ? cb.children().indexOf(playToggle) : 0;
    const at = playIndex >= 0 ? playIndex + 1 : 1;
    self.prevNavBtn = cb.addChild(
      "ChainNavButton",
      {
        navOnClick: onChainPrev,
        controlText: "Previous video",
        buttonClass: "vjs-chain-prev",
        iconSvg: PREV_SVG,
      },
      at,
    ) as ChainNavButton;
    self.nextNavBtn = cb.addChild(
      "ChainNavButton",
      {
        navOnClick: onChainNext,
        controlText: "Next video",
        buttonClass: "vjs-chain-next",
        iconSvg: NEXT_SVG,
      },
      at + 1,
    ) as ChainNavButton;
    setupNavPreviews();
    updateChainNavButtons();
  }

  // YouTube-style hover cards above the prev/next arrows, each showing that
  // video's thumbnail + title. Built once, appended to the player el (so they
  // sit above the control bar), populated on hover.
  function setupNavPreviews() {
    self.prevPreview = buildNavPreview(
      self.prevNavBtn,
      "Previous",
      resolvePrevPreviewItem,
    );
    self.nextPreview = buildNavPreview(
      self.nextNavBtn,
      "Next",
      resolveNextPreviewItem,
    );
  }

  function buildNavPreview(
    btn: ChainNavButton | null,
    label: string,
    resolveItem: () => ChainNavMeta | null,
  ): NavPreview | null {
    if (!btn || !self.player) {
      return null;
    }
    const el = document.createElement("div");
    el.className = "vjs-chain-nav-preview";
    el.setAttribute("aria-hidden", "true");
    el.innerHTML =
      '<div class="vjs-chain-nav-preview__label"></div>' +
      '<div class="vjs-chain-nav-preview__thumb"><img alt="" /></div>' +
      '<div class="vjs-chain-nav-preview__title"></div>';
    (
      el.querySelector(".vjs-chain-nav-preview__label") as HTMLElement
    ).textContent = label;
    self.player.el().appendChild(el);

    const preview: NavPreview = {
      el,
      btn,
      resolveItem,
      hasThumb: false,
      onEnter: () => showNavPreview(preview),
      onLeave: () => hideNavPreview(preview),
    };
    // focus/blur give keyboard users the same preview as hover — the arrows
    // are fully keyboard-operable, so this is parity, not decoration.
    btn.el().addEventListener("mouseenter", preview.onEnter);
    btn.el().addEventListener("mouseleave", preview.onLeave);
    btn.el().addEventListener("focus", preview.onEnter);
    btn.el().addEventListener("blur", preview.onLeave);
    return preview;
  }

  // { thumbUrl, title } for the video the next arrow will advance to (so the
  // preview matches the click): the top chain pick, or — after stepping back —
  // the recorded meta of the already-visited next id. Null when not resolvable.
  function resolveNextPreviewItem(): ChainNavMeta | null {
    const chain = chainStore.getChain();
    const id = chainNav.nextId(chain);
    if (id == null) {
      return null;
    }
    const list = Array.isArray(chain) ? chain : [];
    for (let i = 0; i < list.length; i++) {
      if (String(list[i].experience_id) === String(id)) {
        const experienceData =
          self.props.experienceState &&
          self.props.experienceState.experienceData;
        return {
          thumbUrl: chainThumbUrl(experienceData, list[i].image),
          title: list[i].title,
        };
      }
    }
    return chainNav.metaFor(id);
  }

  // { thumbUrl, title } for the previous video. It's one we've already left, so
  // its poster/title come from what chainNav recorded when it was current — not
  // the current chain (which only lists what's ahead).
  function resolvePrevPreviewItem(): ChainNavMeta | null {
    return chainNav.metaFor(chainNav.prevId());
  }

  // Preload a preview's thumbnail/title into the (hidden) card so hovering shows
  // it instantly. Called whenever the chain/position changes.
  function refreshNavPreview(preview: NavPreview | null) {
    if (!preview) {
      return;
    }
    const item = preview.resolveItem();
    const thumbUrl = item ? item.thumbUrl : null;
    preview.hasThumb = Boolean(thumbUrl);
    if (!thumbUrl) {
      hideNavPreview(preview);
      return;
    }
    const img = preview.el.querySelector(".vjs-chain-nav-preview__thumb img");
    const titleEl = preview.el.querySelector<HTMLElement>(
      ".vjs-chain-nav-preview__title",
    );
    if (img && img.getAttribute("src") !== thumbUrl) {
      img.setAttribute("src", thumbUrl);
    }
    if (titleEl) {
      const title = cleanChainTitle((item && item.title) || "") || "";
      titleEl.textContent = title;
      titleEl.style.display = title ? "" : "none";
    }
  }

  function showNavPreview(preview: NavPreview | null) {
    if (!preview || !preview.el || !preview.btn || !self.player) {
      return;
    }
    // Nothing to preview at the end of the history / chain, or with no thumb.
    if (preview.btn.hasClass("vjs-disabled") || !preview.hasThumb) {
      return;
    }
    const el = preview.el;
    el.classList.add("is-visible"); // measurable before positioning
    const playerRect = self.player.el().getBoundingClientRect();
    const btnRect = preview.btn.el().getBoundingClientRect();
    const width = el.offsetWidth;
    let left = btnRect.left - playerRect.left + btnRect.width / 2 - width / 2;
    left = Math.max(8, Math.min(left, playerRect.width - width - 8));
    el.style.left = `${left}px`;
    el.style.bottom = `${playerRect.bottom - btnRect.top + 8}px`;
  }

  function hideNavPreview(preview: NavPreview | null) {
    if (preview && preview.el) {
      preview.el.classList.remove("is-visible");
    }
  }

  // Show the arrows only when the current video is part of a recommendation
  // chain — otherwise hide the pair entirely. Within a chain, enable/disable for
  // the current position: prev off at the start of the history, next off when
  // there's neither a visited-next nor a chain pick.
  function updateChainNavButtons() {
    const chain = chainStore.getChain();
    const inChain = chainNav.inChain(chain);
    if (self.prevNavBtn) {
      self.prevNavBtn[inChain ? "show" : "hide"]();
      self.prevNavBtn.setEnabled(chainNav.canPrev());
    }
    if (self.nextNavBtn) {
      self.nextNavBtn[inChain ? "show" : "hide"]();
      self.nextNavBtn.setEnabled(chainNav.canNext(chain));
    }
    refreshNavPreview(self.prevPreview);
    refreshNavPreview(self.nextPreview);
  }

  function onChainStoreChange() {
    updateChainNavButtons();
  }

  function onChainPrev() {
    const id = chainNav.prevId();
    if (id == null || !self.props.onApplicationEvent) {
      return;
    }
    // Don't leave the preview hanging over the swap it just triggered.
    hideNavPreview(self.prevPreview);
    analytics.track("Chain Nav Prev Clicked", {
      pix_from_idexperience: self.idexperience,
      pix_to_idexperience: id,
    });
    self.props.onApplicationEvent({
      name: eventNames.CHAIN_ADVANCE,
      idexperience: id,
      region: "Chain Nav Prev",
    });
  }

  function onChainNext() {
    const id = chainNav.nextId(chainStore.getChain());
    if (id == null || !self.props.onApplicationEvent) {
      return;
    }
    // Don't leave the preview hanging over the swap it just triggered.
    hideNavPreview(self.nextPreview);
    analytics.track("Chain Nav Next Clicked", {
      pix_from_idexperience: self.idexperience,
      pix_to_idexperience: id,
    });
    self.props.onApplicationEvent({
      name: eventNames.CHAIN_ADVANCE,
      idexperience: id,
      region: "Chain Nav Next",
    });
  }

  // The detach part for the arrows: the previews and the buttons close over
  // this mount, so they are torn down here and rebuilt by the next mount.
  function detachChainNav() {
    // Nav preview hover cards: remove their listeners + DOM (rebuilt on re-host).
    [self.prevPreview, self.nextPreview].forEach(preview => {
      if (preview && preview.btn && preview.btn.el()) {
        const btnEl = preview.btn.el();
        btnEl.removeEventListener("mouseenter", preview.onEnter);
        btnEl.removeEventListener("mouseleave", preview.onLeave);
        btnEl.removeEventListener("focus", preview.onEnter);
        btnEl.removeEventListener("blur", preview.onLeave);
      }
      if (preview && preview.el && preview.el.parentNode) {
        preview.el.parentNode.removeChild(preview.el);
      }
    });
    self.prevPreview = null;
    self.nextPreview = null;
    // Chain-nav buttons live on the persistent control bar; remove + dispose them
    // so the next mount re-adds its own (bound to it) with no duplication.
    // Skipped on a disposed player: the buttons went down with it, and
    // removeChild() on the disposed control bar would throw.
    if (self.player && !self.player.isDisposed() && self.player.controlBar) {
      if (self.prevNavBtn) {
        try {
          self.player.controlBar.removeChild(self.prevNavBtn);
          self.prevNavBtn.dispose();
        } catch {
          /* ignore */
        }
      }
      if (self.nextNavBtn) {
        try {
          self.player.controlBar.removeChild(self.nextNavBtn);
          self.nextNavBtn.dispose();
        } catch {
          /* ignore */
        }
      }
    }
    self.prevNavBtn = null;
    self.nextNavBtn = null;
  }

  return {
    setupChainNavButtons,
    updateChainNavButtons,
    onChainStoreChange,
    detachChainNav,
  };
}

export type ChainNavigation = ReturnType<typeof createChainNavigation>;

/** The chain prev/next arrows of one VideoJs mount; stable for the mount. */
export function useChainNavigation(self: VideoJsSelf): ChainNavigation {
  const [chain] = useState(() => createChainNavigation(self));
  return chain;
}
